import React, { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateProfile, useProfile, useUpdateProfile } from '@/hooks/useProfile';
import { useJoinCohort, useCohorts } from '@/hooks/useCohorts';
import { useSubmitOnboardingResponses, useOnboardingQuestions } from '@/hooks/useOnboarding';
import { useCreatePost } from '@/hooks/usePosts';
import { useChannelBySlug } from '@/hooks/useChannels';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Linkedin, Camera } from 'lucide-react';
import AvatarCropDialog from '@/components/AvatarCropDialog';
import CityAutocomplete from '@/components/onboarding/CityAutocomplete';
import { CONTRIBUTION_PRESETS, INTEREST_PRESETS } from '@/lib/onboarding-presets';

const BUBBLE_COLORS = [
  { bg: 'rgba(233, 30, 99, 0.18)', border: 'rgba(233, 30, 99, 0.5)', text: '#f06292' },
  { bg: 'rgba(124, 77, 255, 0.18)', border: 'rgba(124, 77, 255, 0.5)', text: '#b388ff' },
  { bg: 'rgba(16, 185, 129, 0.18)', border: 'rgba(16, 185, 129, 0.5)', text: '#6ee7b7' },
  { bg: 'rgba(245, 158, 11, 0.18)', border: 'rgba(245, 158, 11, 0.5)', text: '#fbbf24' },
  { bg: 'rgba(14, 165, 233, 0.18)', border: 'rgba(14, 165, 233, 0.5)', text: '#7dd3fc' },
];

export default function OnboardingPage() {
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();
  const metadata = user?.user_metadata ?? {};
  const isLinkedIn = user?.app_metadata?.provider === 'linkedin_oidc';
  const isGoogle = user?.app_metadata?.provider === 'google';
  const isOAuth = isLinkedIn || isGoogle;
  const linkedInAvatar = metadata.avatar_url || metadata.picture || '';

  const [formData, setFormData] = useState({
    full_name: metadata.full_name || metadata.name || '',
    role: '',
    company: '',
    industry: '',
    contributing: '',
    interested_in: '',
    city_cohort: '',
    linkedin_url: isLinkedIn ? 'https://www.linkedin.com/in/' : '',
  });
  const [isMentor, setIsMentor] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(linkedInAvatar);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Check if this is a "request access" signup (no invite code)
  // If an invite code exists, always treat as invite-code flow regardless of stale flag
  const hasInviteCode = !!localStorage.getItem('qbn_invite_code');
  const isRequestAccess = !hasInviteCode && localStorage.getItem('qbn_request_access') === 'true';

  const navigate = useNavigate();
  const { toast } = useToast();
  const createProfile = useCreateProfile();
  const updateProfile = useUpdateProfile();
  const { data: existingProfile } = useProfile();
  const { data: cohorts } = useCohorts();
  const joinCohort = useJoinCohort();
  const submitResponses = useSubmitOnboardingResponses();
  const createPost = useCreatePost();
  const { data: introsChannel } = useChannelBySlug('intros');
  const { data: questions } = useOnboardingQuestions();

  // Redirect to login if not authenticated (after all hooks)
  if (!loading && !user) {
    return <Navigate to="/auth" replace />;
  }

  // Helper: toggle a preset label in a comma-separated text field
  const toggleInField = (field: 'contributing' | 'interested_in', label: string) => {
    setFormData((prev) => {
      const current = prev[field]
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const exists = current.includes(label);
      const updated = exists ? current.filter((s) => s !== label) : [...current, label];
      return { ...prev, [field]: updated.join(', ') };
    });
  };

  const fieldIncludes = (field: string, label: string) => {
    return field
      .split(',')
      .map((s) => s.trim())
      .includes(label);
  };

  const getColorIndex = (field: string, label: string) => {
    const items = field.split(',').map((s) => s.trim()).filter(Boolean);
    const idx = items.indexOf(label);
    return idx >= 0 ? idx % BUBBLE_COLORS.length : 0;
  };

  const updateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Please upload a JPEG, PNG, GIF, or WebP image.', variant: 'destructive' });
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum size is 10MB.', variant: 'destructive' });
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleCroppedUpload = async (blob: Blob) => {
    if (!user?.id) return;
    setCropImageSrc(null);
    setUploadingAvatar(true);
    try {
      const fileName = `avatar-${Date.now()}.jpg`;
      const storagePath = `avatars/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('post-media')
        .upload(storagePath, blob, { contentType: 'image/jpeg', cacheControl: '31536000', upsert: true });
      if (uploadError) throw uploadError;

      setAvatarUrl(storagePath);
      toast({ title: 'Photo uploaded!' });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const getMissingRequiredFields = () => {
    const required = [
      { key: 'full_name', label: 'Full name' },
      { key: 'role', label: 'Current title' },
      { key: 'company', label: 'Current company' },
      { key: 'industry', label: 'Primary function' },
      { key: 'contributing', label: 'Contributing to QBN' },
      { key: 'interested_in', label: 'Interested in through QBN' },
      { key: 'city_cohort', label: 'City cohort' },
      { key: 'linkedin_url', label: 'LinkedIn URL' },
    ] as const;

    return required
      .filter((field) => !String(formData[field.key]).trim())
      .map((field) => field.label);
  };

  const upsertProfile = async (membershipStatus: 'approved' | 'pending' | 'applied') => {
    const contributing = formData.contributing.trim();
    const interestedIn = formData.interested_in.trim();

    // Mark invite code as used if one was stored by the Auth page
    const storedInviteCode = localStorage.getItem('qbn_invite_code');
    if (storedInviteCode && user?.id) {
      await supabase.rpc('use_invite_code', {
        code_to_use: storedInviteCode,
        user_id_using: user.id,
      });
      localStorage.removeItem('qbn_invite_code');
    }

    // Build structured bio from all fields
    const bioParts: string[] = [];
    if (formData.role) bioParts.push(`Current Title: ${formData.role}`);
    if (formData.company) bioParts.push(`Current Company: ${formData.company}`);
    if (formData.industry) bioParts.push(`Primary Function: ${formData.industry}`);
    if (contributing) bioParts.push(`Contributing to QBN: ${contributing}`);
    if (interestedIn) bioParts.push(`Interested in through QBN: ${interestedIn}`);
    const bio = bioParts.join('\n\n') || null;

    const profileData = {
      full_name: formData.full_name,
      role: formData.role || null,
      company: formData.company || null,
      city_cohort: formData.city_cohort || 'Unknown',
      linkedin_url: formData.linkedin_url || null,
      avatar_url: avatarUrl || null,
      is_mentor: isMentor,
      industry: formData.industry || null,
      bio,
      onboarding_completed: true,
      membership_status: membershipStatus,
    };

    if (existingProfile) {
      await updateProfile.mutateAsync(profileData);
    } else {
      await createProfile.mutateAsync(profileData);
    }

    return { bio };
  };

  const handleComplete = async () => {
    try {
      const missing = getMissingRequiredFields();
      if (missing.length > 0) {
        // For request access users, all fields are required - no "view-only" option
        if (isRequestAccess) {
          toast({
            title: 'Please complete all fields',
            description: `Missing: ${missing.join(', ')}`,
            variant: 'destructive',
          });
          return;
        }
        // For invite code users, offer the view-only option
        setMissingFields(missing);
        setIncompleteDialogOpen(true);
        return;
      }

      // Request access users get 'applied' status for admin review
      const membershipStatus = isRequestAccess ? 'applied' : 'approved';
      const { bio } = await upsertProfile(membershipStatus);

      // Clear the request access flag
      if (isRequestAccess) {
        localStorage.removeItem('qbn_request_access');
      }

      // Auto-join cohort if one matches the selected city (only for approved users)
      if (!isRequestAccess && formData.city_cohort) {
        const matchingCohort = cohorts?.find(
          (c) =>
            c.city?.toLowerCase() === formData.city_cohort.toLowerCase() ||
            c.name.toLowerCase() === formData.city_cohort.toLowerCase()
        );
        if (matchingCohort) {
          await joinCohort.mutateAsync({
            cohortId: matchingCohort.id,
            isPrimary: true,
          });
        }
      }

      // Save bubble selections as onboarding responses
      const responses: { questionId: string; response: any }[] = [];

      const contributionQuestion = questions?.find(
        (q) => q.question.toLowerCase().includes('contribut') || q.question.toLowerCase().includes('give')
      );
      const interestQuestion = questions?.find(
        (q) => q.question.toLowerCase().includes('interest') || q.question.toLowerCase().includes('here for')
      );

      const contributing = formData.contributing.trim();
      const interestedIn = formData.interested_in.trim();

      if (contributionQuestion && contributing) {
        responses.push({ questionId: contributionQuestion.id, response: contributing });
      }
      if (interestQuestion && interestedIn) {
        responses.push({ questionId: interestQuestion.id, response: interestedIn });
      }

      if (responses.length === 0 && questions && questions.length >= 2) {
        if (contributing) {
          responses.push({ questionId: questions[0].id, response: contributing });
        }
        if (interestedIn) {
          responses.push({ questionId: questions[1].id, response: interestedIn });
        }
      }

      if (responses.length > 0) {
        await submitResponses.mutateAsync(responses);
      }

      // Auto-post intro to the intros channel (only for approved users)
      if (!isRequestAccess && introsChannel && bio) {
        const introContent = formData.linkedin_url
          ? `${bio}\n\n${formData.linkedin_url}`
          : bio;
        await createPost.mutateAsync({
          content: introContent,
          channel_id: introsChannel.id,
          cohort_tag: formData.city_cohort || 'global',
          category: 'discussion',
        });
      }

      if (isRequestAccess) {
        toast({
          title: 'Application submitted!',
          description: 'Your profile is under review. We\'ll notify you once approved.',
        });
        navigate('/applications');
      } else {
        toast({
          title: 'Welcome to QBN!',
          description: 'Your profile is set up. Start connecting with your network.',
        });
        navigate('/home');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleContinueViewOnly = async () => {
    try {
      if (!formData.full_name.trim()) {
        toast({
          title: 'Name required',
          description: 'Please add your name to continue with view-only access.',
          variant: 'destructive',
        });
        return;
      }
      await upsertProfile('pending');
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast({
        title: 'Profile saved',
        description: 'You can continue with view-only access for now.',
      });
      navigate('/home');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIncompleteDialogOpen(false);
    }
  };

  const isSubmitting = createProfile.isPending || updateProfile.isPending || joinCohort.isPending || submitResponses.isPending || createPost.isPending;

  return (
    <div className="min-h-screen overflow-y-auto p-6" style={{ background: 'hsl(217, 69%, 7%)' }}>
      <div className="flex justify-center py-8">
        <Card className="w-full max-w-lg border" style={{ background: 'hsl(217, 50%, 12%)', borderColor: 'hsl(217, 40%, 22%)' }}>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div
                className="relative group cursor-pointer"
                onClick={() => !isOAuth && avatarInputRef.current?.click()}
              >
                <Avatar className="h-14 w-14">
                  <SignedAvatarImage src={avatarUrl} />
                  <AvatarFallback style={{ background: 'linear-gradient(135deg, hsl(340, 82%, 52%), hsl(255, 100%, 65%))' }}>
                    <svg className="h-8 w-8 text-white/80" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </AvatarFallback>
                </Avatar>
                {!isOAuth && (
                  <>
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      {uploadingAvatar ? (
                        <Loader2 className="h-5 w-5 text-white animate-spin" />
                      ) : (
                        <Camera className="h-5 w-5 text-white" />
                      )}
                    </div>
                    {!avatarUrl && (
                      <div className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full flex items-center justify-center" style={{ background: 'hsl(217, 50%, 12%)' }}>
                        <Camera className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </>
                )}
              </div>
              <div>
                <CardTitle className="text-xl text-white">Welcome to QBN</CardTitle>
                <CardDescription style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {isLinkedIn ? (
                    <span className="flex items-center gap-1.5">
                      <Linkedin className="h-3.5 w-3.5" />
                      Signed in with LinkedIn
                    </span>
                  ) : isGoogle ? (
                    'Signed in with Google'
                  ) : (
                    avatarUrl ? 'Looking good!' : 'Add a photo to get started'
                  )}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-5">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="full_name" className="onboarding-label">Full Name *</Label>
              <Input
                id="full_name"
                className="onboarding-input"
                placeholder="Your full name"
                value={formData.full_name}
                onChange={(e) => updateFormData('full_name', e.target.value)}
              />
            </div>

            {/* Title & Company side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="role" className="onboarding-label">Current Title</Label>
                <Input
                  id="role"
                  className="onboarding-input"
                  placeholder="e.g., Senior VP"
                  value={formData.role}
                  onChange={(e) => updateFormData('role', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company" className="onboarding-label">Current Company</Label>
                <Input
                  id="company"
                  className="onboarding-input"
                  placeholder="e.g., Wells Fargo"
                  value={formData.company}
                  onChange={(e) => updateFormData('company', e.target.value)}
                />
              </div>
            </div>

            {/* Primary Function */}
            <div className="space-y-1.5">
              <Label htmlFor="industry" className="onboarding-label">Primary Function</Label>
              <Input
                id="industry"
                className="onboarding-input"
                placeholder="e.g., Operational Risk, Product Management"
                value={formData.industry}
                onChange={(e) => updateFormData('industry', e.target.value)}
              />
            </div>

            {/* Contributing — bubbles then text input */}
            <div className="space-y-2">
              <Label htmlFor="contributing" className="onboarding-label">How do you see yourself contributing to QBN?</Label>
              <div className="flex flex-wrap gap-2">
                {CONTRIBUTION_PRESETS.map((preset) => {
                  const isSelected = fieldIncludes(formData.contributing, preset.label);
                  const colorIdx = getColorIndex(formData.contributing, preset.label);
                  const color = BUBBLE_COLORS[colorIdx];
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => toggleInField('contributing', preset.label)}
                      style={isSelected
                        ? { background: color.bg, borderColor: color.border, color: color.text }
                        : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)' }
                      }
                      className="rounded-full px-3 py-1 text-xs font-medium border transition-all duration-200 cursor-pointer select-none"
                    >
                      <span className="mr-1.5">{preset.emoji}</span>{preset.label}
                    </button>
                  );
                })}
              </div>
              <Input
                id="contributing"
                className="onboarding-input"
                placeholder="or type your own..."
                value={formData.contributing}
                onChange={(e) => updateFormData('contributing', e.target.value)}
              />
            </div>

            {/* Interested in — bubbles then text input */}
            <div className="space-y-2">
              <Label htmlFor="interested_in" className="onboarding-label">What are you most interested in through QBN?</Label>
              <div className="flex flex-wrap gap-2">
                {INTEREST_PRESETS.map((preset) => {
                  const isSelected = fieldIncludes(formData.interested_in, preset.label);
                  const colorIdx = getColorIndex(formData.interested_in, preset.label);
                  const color = BUBBLE_COLORS[colorIdx];
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => toggleInField('interested_in', preset.label)}
                      style={isSelected
                        ? { background: color.bg, borderColor: color.border, color: color.text }
                        : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)' }
                      }
                      className="rounded-full px-3 py-1 text-xs font-medium border transition-all duration-200 cursor-pointer select-none"
                    >
                      <span className="mr-1.5">{preset.emoji}</span>{preset.label}
                    </button>
                  );
                })}
              </div>
              <Input
                id="interested_in"
                className="onboarding-input"
                placeholder="or type your own..."
                value={formData.interested_in}
                onChange={(e) => updateFormData('interested_in', e.target.value)}
              />
            </div>

            {/* Location */}
            <div className="space-y-1.5">
              <Label htmlFor="city_cohort" className="onboarding-label">Location</Label>
              <div className="onboarding-city-input">
                <CityAutocomplete
                  value={formData.city_cohort}
                  onChange={(city) => updateFormData('city_cohort', city)}
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div className="space-y-1.5">
              <Label htmlFor="linkedin_url" className="onboarding-label">LinkedIn URL</Label>
              <Input
                id="linkedin_url"
                className="onboarding-input"
                placeholder="https://linkedin.com/in/yourprofile"
                value={formData.linkedin_url}
                onChange={(e) => updateFormData('linkedin_url', e.target.value)}
              />
            </div>

            {/* Matchups toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="space-y-0.5">
                <Label htmlFor="is_mentor" className="text-sm font-medium text-white">
                  Open to Matchups
                </Label>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Get matched based on your skillsets and interests
                </p>
              </div>
              <Switch
                id="is_mentor"
                checked={isMentor}
                onCheckedChange={setIsMentor}
              />
            </div>

            {/* Submit */}
            <Button
              className="w-full text-white font-semibold"
              style={{
                background: 'linear-gradient(135deg, hsl(340, 82%, 52%), hsl(255, 100%, 65%))',
                boxShadow: '0 0 20px rgba(233, 30, 99, 0.25)',
              }}
              onClick={handleComplete}
              disabled={!formData.full_name || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      <AvatarCropDialog
        open={!!cropImageSrc}
        imageSrc={cropImageSrc || ''}
        onCrop={handleCroppedUpload}
        onCancel={() => {
          setCropImageSrc(null);
          if (avatarInputRef.current) avatarInputRef.current.value = '';
        }}
      />

      <AlertDialog open={incompleteDialogOpen} onOpenChange={setIncompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete your profile?</AlertDialogTitle>
            <AlertDialogDescription>
              Your profile isn't complete yet. Finish it now for full access, or skip and browse with view-only access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row items-center gap-2">
            <AlertDialogAction>Complete now</AlertDialogAction>
            <button
              type="button"
              className="text-sm text-muted-foreground underline hover:text-foreground"
              onClick={handleContinueViewOnly}
            >
              Skip (view-only access)
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
